import type { JSX } from 'react';

import React, { Children } from 'react';

import { classNames } from '../../utils/class-names';

import styles from './buttons.module.css';

export type ButtonProps = {
  small?: boolean;
  to?: string;
  component?: any;
} & JSX.IntrinsicElements['button'] &
  JSX.IntrinsicElements['a'];

function Button({ small, to, component, ...props }: ButtonProps) {
  const isExternalLink = String(to).startsWith('http');
  const isInternalLink = Boolean(to) && !isExternalLink;

  let Element: 'button' | 'a' | any = 'button';
  if (isInternalLink && !props.disabled && component) Element = component;
  if (isExternalLink && !props.disabled) Element = 'a';

  return (
    <Element
      to={!props.disabled && isInternalLink ? to : undefined}
      href={!props.disabled && (isExternalLink || component) ? to : undefined}
      target={isExternalLink ? '_blank' : undefined}
      rel={isExternalLink ? 'noopener noreferrer' : undefined}
      tabIndex={props.disabled ? -1 : undefined}
      {...props}
      className={classNames(styles.button, small && styles.small, props.disabled && styles.disabled, props.className)}
    />
  );
}

export function PrimaryButton(props: ButtonProps) {
  return <Button {...props} className={classNames(styles.primary, props.className)} />;
}

export function SecondaryButton(props: ButtonProps) {
  return <Button {...props} className={classNames(styles.secondary, props.className)} />;
}

export function GhostButton(props: ButtonProps) {
  return <Button {...props} className={classNames(styles.ghost, props.className)} />;
}

export function AlertButton(props: ButtonProps) {
  return <Button {...props} className={classNames(styles.alert, props.className)} />;
}

export function WarningButton(props: ButtonProps) {
  return <Button {...props} className={classNames(styles.warning, props.className)} />;
}

export type IconButtonProps = {
  'aria-label': string;
  active?: boolean;
  rounded?: boolean;
  naked?: boolean;
} & ButtonProps;

export function IconButton({ active, rounded, naked, ...props }: IconButtonProps) {
  return (
    <Button
      {...props}
      className={classNames(
        styles.icon,
        active && styles.active,
        rounded && styles.rounded,
        naked && styles.naked,
        props.className
      )}
    />
  );
}

export type MenuButtonProps = {
  highlighted?: boolean;
  active?: boolean;
  selected?: boolean;
} & ButtonProps;

export function MenuButton({ highlighted, active, selected, ...props }: MenuButtonProps) {
  return (
    <Button
      {...props}
      className={classNames(
        styles.menu,
        highlighted && styles.highlighted,
        active && styles.active,
        selected && styles.selected,
        Children.count(props.children) === 1 && styles.singleChild,
        props.className
      )}
    />
  );
}

export function LinkStyleButton(props: ButtonProps) {
  return <Button {...props} className={classNames(styles.linkStyle, props.className)} />;
}
